import React, { useRef, useCallback } from "react";
import {
  StyledEditableTitle,
  StyledAutoGrow,
  StyledTitleInput,
  StyledIdleText,
} from "./styled";

/**
 * Inline-editable title. A CONTROLLED input — value/onChange per keystroke,
 * the same contract as every other input comp — that only LOOKS like a bare
 * heading text node (typo class cascades in, no box until focus). Being a
 * real input means React owns the caret, so none of the contentEditable
 * babysitting the old version needed (edit-mode state, frozen render child,
 * a separate live-change channel) exists anymore: live streaming IS onChange.
 *
 * It auto-grows to its content via the CSS inline-grid mirror trick
 * (StyledAutoGrow) so trailing siblings — e.g. a collab lock chip — sit
 * right after the text, and truncates with an ellipsis at the container cap.
 *
 * Commit semantics layered on top of the live stream:
 *  - Enter commits (blurs). Escape reverts to the focus-time value.
 *  - Blurring an EMPTIED title reverts — a doc shouldn't lose its name to an
 *    accidental select-all + delete. The revert flows through onChange, so
 *    live consumers (collab peers) snap back too.
 *  - Blur trims: surrounding whitespace never persists.
 */
export const InlineEditableInput = ({
  value,
  onChange,
  readOnly,
  style,
  id,
  placeholder = "",
  /** Typography class of the text itself — callers match their own header. */
  typoClassName = "UI_TITLE_SM",
}) => {
  const inputRef = useRef(null);
  const valueOnFocus = useRef(value);
  // Escape already reverted via onChange; the blur that follows must not
  // re-commit the stale pre-revert prop value over it.
  const revertedRef = useRef(false);

  const handleFocus = useCallback(() => {
    valueOnFocus.current = value;
    revertedRef.current = false;
  }, [value]);

  const handleBlur = useCallback(() => {
    if (revertedRef.current) {
      revertedRef.current = false;
      return;
    }
    const trimmed = (value ?? "").trim();
    if (!trimmed) {
      onChange(id, valueOnFocus.current ?? "");
      return;
    }
    if (trimmed !== value) onChange(id, trimmed);
  }, [value, onChange, id]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        inputRef.current?.blur();
      } else if (e.key === "Escape") {
        e.preventDefault();
        revertedRef.current = true;
        onChange(id, valueOnFocus.current ?? "");
        inputRef.current?.blur();
      }
    },
    [onChange, id]
  );

  return (
    <StyledEditableTitle
      className={typoClassName}
      style={style}
      readOnly={readOnly}
      // reveals the full text when it truncates; the placeholder isn't real
      // text and has nothing to reveal, so it stays out of the tooltip
      title={value || undefined}
    >
      {readOnly ? (
        // plain selectable text — no caret, no focus ring, no placeholder
        <StyledIdleText>{value}</StyledIdleText>
      ) : (
        <StyledAutoGrow data-value={value || placeholder}>
          <StyledTitleInput
            id={id}
            ref={inputRef}
            value={value ?? ""}
            placeholder={placeholder}
            onChange={(e) => onChange(id, e.target.value)}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
          />
        </StyledAutoGrow>
      )}
    </StyledEditableTitle>
  );
};
